import type { Work } from "@/types/work";
import { formatViews, workTypeLabel } from "@/lib/workUtils";

interface DetailHeaderProps {
  work: Work;
  favorited: boolean;
  onFavorite: () => void;
  onShare: () => void;
  onPlay: () => void;
  onSearchActor: (name: string) => void;
}

export default function DetailHeader({
  work,
  favorited,
  onFavorite,
  onShare,
  onPlay,
  onSearchActor,
}: DetailHeaderProps) {
  return (
    <section className="relative w-full">
      <div className="relative h-64 w-full overflow-hidden md:h-80">
        <img
          src={work.backdrop}
          alt={`${work.title}剧照`}
          title={`${work.title} 剧情介绍`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/60 to-primary-950/40" />
        <div className="absolute inset-0 mx-auto flex w-full flex-col justify-end px-4 pb-20 md:px-8 md:pb-24">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
              {workTypeLabel(work.type)}
            </span>
            <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100 backdrop-blur">
              {work.status}
            </span>
            <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-bold text-accent-300 backdrop-blur">
              <i className="ri-star-fill" />
              {work.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-background-50 md:text-5xl">
            {work.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-background-200 md:text-base">
            “{work.tagline}”
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 md:px-8">
        <div className="-mt-14 grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-50 p-5 md:-mt-16 md:grid-cols-[220px_1fr] md:gap-8 md:p-8">
          <div className="mx-auto w-36 shrink-0 overflow-hidden rounded-lg border border-background-300 bg-background-200 md:mx-0 md:w-full">
            <div className="aspect-[2/3] w-full">
              <img
                src={work.poster}
                alt={`${work.title}海报`}
                title={`${work.title} 剧情介绍与演员信息`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-base text-primary-600" />
                {work.year}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-base text-primary-600" />
                {work.region}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-timer-line text-base text-primary-600" />
                {work.type === "tv" ? `${work.episodes} 集` : work.duration}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-base text-primary-600" />
                {formatViews(work.views)} 次浏览
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {work.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                >
                  {genre}
                </span>
              ))}
            </div>

            <div className="mt-5 space-y-2 text-sm">
              <p className="text-foreground-700">
                <span className="text-foreground-500">导演：</span>
                <span className="font-medium">{work.director}</span>
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-foreground-700">
                <span className="text-foreground-500">主演：</span>
                {work.cast.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => onSearchActor(name)}
                    className="font-medium text-primary-700 underline-offset-4 transition-colors hover:text-accent-600 hover:underline"
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onPlay}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-circle-line text-lg" />
                在线播放
              </button>
              <button
                type="button"
                onClick={onFavorite}
                className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-semibold transition-colors ${
                  favorited
                    ? "border-accent-500 bg-accent-100 text-accent-800"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={onShare}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-forward-line text-lg" />
                分享
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}